import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { environments } from '../../../environments/environments';
import { RoleId } from '../../core/models/role.model';

// AuthService for handling authentication

export interface UserData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  client_id?: number | null;
  roles?: string[];
  permissions?: string[];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: UserData;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  // Private subjects for state management
  private _isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private _tokenSubject = new BehaviorSubject<string | null>(null);
  private _userDataSubject = new BehaviorSubject<UserData | null>(null);
  private _roleSubject = new BehaviorSubject<string | null>(null);

  // Public observables
  public isLoggedIn$ = this._isLoggedInSubject.asObservable();
  public token$ = this._tokenSubject.asObservable();
  public userData$ = this._userDataSubject.asObservable();
  public role$ = this._roleSubject.asObservable();

  // Computed values for easy access
  public get isLoggedInValue(): boolean {
    return this._isLoggedInSubject.value;
  }

  public get tokenValue(): string | null {
    return this._tokenSubject.value;
  }

  public get userDataValue(): UserData | null {
    return this._userDataSubject.value;
  }

  public get roleValue(): string | null {
    return this._roleSubject.value;
  }

  constructor() {
    // Initialize from localStorage if available
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    const token = localStorage.getItem(environments.localStorageKeys.token);
    const userData = localStorage.getItem(environments.localStorageKeys.userData);

    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        this._tokenSubject.next(token);
        this._userDataSubject.next(parsedUserData);
        this._roleSubject.next(parsedUserData.role);
        this._isLoggedInSubject.next(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        this.clearAuthData();
      }
    }
  }

  public onLogin(credentials: LoginCredentials): Observable<any> {
    const apiUrl = `${environments.appUrls.backend}/login`;

    return this.http.post<LoginResponse>(apiUrl, credentials).pipe(
      tap((response: LoginResponse) => {
        if (response.user && response.token) {
          const { user, token } = response;

          // Store in localStorage
          localStorage.setItem(environments.localStorageKeys.token, token);
          localStorage.setItem(environments.localStorageKeys.userData, JSON.stringify(user));

          // Update subjects
          this._tokenSubject.next(token);
          this._userDataSubject.next(user);
          this._roleSubject.next(user.role);
          this._isLoggedInSubject.next(true);

          // Navigate to dashboard
          this.router.navigate(['/admin/dashboard']);
        }
      }),
      catchError((error) => {
        console.error('Login error:', error);
        throw error;
      })
    );
  }

  public onLogout(): Observable<any> {
    const apiUrl = `${environments.appUrls.backend}/logout`;

    return this.http.get(apiUrl).pipe(
      tap(() => {
        this.clearAuthData();
        this.router.navigate(['/login']);
      }),
      catchError((error) => {
        console.error('Logout error:', error);
        // Even if logout fails on server, clear local data
        this.clearAuthData();
        this.router.navigate(['/login']);
        throw error;
      })
    );
  }

  public clearSession(): void {
    localStorage.removeItem(environments.localStorageKeys.token);
    localStorage.removeItem(environments.localStorageKeys.userData);

    this._tokenSubject.next(null);
    this._userDataSubject.next(null);
    this._roleSubject.next(null);
    this._isLoggedInSubject.next(false);
  }

  private clearAuthData(): void {
    this.clearSession();
  }

  public isAuthenticated(): boolean {
    return this.isLoggedInValue && !!this.tokenValue;
  }

  public hasRole(role: string): boolean {
    return this.roleValue === role;
  }

  public hasAnyRole(roles: string[]): boolean {
    return roles.includes(this.roleValue || '');
  }

  public hasRoleId(roleId: RoleId): boolean {
    const roleNameMap: Record<RoleId, string> = {
      [RoleId.ADMINISTRADOR]: 'administrador',
      [RoleId.GESTOR]: 'gestor',
      [RoleId.EDITOR]: 'editor'
    };
    return this.roleValue?.toLowerCase() === roleNameMap[roleId];
  }

  //Verifica si el usuario tiene un permiso específico por su slug.
  public isPermit(permission: string): boolean {
    const permissions = this._userDataSubject.value?.permissions ?? [];
    return permissions.includes(permission);
  }

  //Verifica múltiples permisos con operador AND u OR.
  public hasPermissions(operator: 'OR' | 'AND', ...slugs: string[]): boolean {
    const checks = slugs.map(slug => this.isPermit(slug));
    return operator === 'AND'
      ? checks.every(Boolean)
      : checks.some(Boolean);
  }

  // Call this after updating a role to reflect permission changes immediately.
  public refreshCurrentUser(): Observable<any> {
    const apiUrl = `${environments.appUrls.backend}/me`;
    return this.http.get<{ user: UserData }>(apiUrl).pipe(
      tap((response) => {
        const user = response.user;
        if (user) {
          localStorage.setItem(environments.localStorageKeys.userData, JSON.stringify(user));
          this._userDataSubject.next(user);
          this._roleSubject.next(user.role);
        }
      }),
      catchError((error) => {
        console.error('Error refreshing user data:', error);
        throw error;
      })
    );
  }
}
